// Core

import {
  AttributeClassification,
  type AttributeType,
  languageOptions,
  MARKETPLACE_TO_DEFAULT_LANGUAGE,
  type Marketplace,
} from '@amzn/journeys-common';
import { faPaperPlane, faSave } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { h1Class, loadingOverlayContainerClass, textMutedDarkClass, versionedItemTableStyles } from 'app.styles';
import type { NavConfirmNeeded } from 'app.types';
import { isLaunchPermOn } from 'app.util';
import { AppDate } from 'components/AppDate/AppDate';
import { ErrorAlert } from 'components/ErrorAlert';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { STATUSES, Status } from 'components/Status/Status';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
// Utility
import { find, isEmpty, isEqual, keys, map, merge, pick, reduce, sortBy, uniq } from 'es-toolkit/compat';
import { getSymphonyRealmFromMarketplace } from 'features/symphony/api.util';
import type { Decorator, FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
// Types
import {
  type AttributeFieldsFragment,
  AttributesDocument,
  type AttributesQuery,
  PricingDocument,
  PricingEnum,
  type PricingInfo,
  type PricingQuery,
  type PricingTabAsinFragment,
  useAttributesQuery,
  useLastPublishedPricingQuery,
  useLastPublishedVersionsQuery,
  usePricingQuery,
  useSaveAttributesMutation,
  useSavePricingMutation,
  useValidatePublishedPricingMutation,
} from 'generated/graphql';
import { type FC, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import { Field, type FieldRenderProps, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
// Styles
import { classes, stylesheet } from 'typestyle';
import { AttributeList } from '../attributes/AttributeList';
import { AttributePublishingErrorsModal } from '../attributes/AttributesPublishingErrorsModal';
import { AttributePublishModal } from '../attributes/AttributesPublishModal';
import type { AttributeFormValue, AttributeKeyToObject, AttributeListFormValues } from '../attributes/attributes.types';
import {
  attributeDataToFormValues,
  getAttributesToSave,
  getCompositeAttrKey,
  getDefaultAttributeValue,
  getPricingKeysForMarketplace,
  getPricingKeyValueForMarketplace,
  getUpdatedAttributes,
  hasPublishingErrors,
  reducePublishingIssues,
} from '../attributes/attributes.util';
// UI components
import { PricingErrorModal } from './PricingErrorModal';
import { PricingPublishModal } from './PricingPublishModal';
import { type PricingFormValues, pricingInfoToLabel, pricingInfoTypeOrder } from './pricing.types';
import { getPricingInfoToSave, getUpdatedPricing } from './pricing.util';

export type PricingTabProps = {
  asin: PricingTabAsinFragment;
  attributes: AttributeFieldsFragment[];
  marketplace: Marketplace;
  productId: string;
  navConfirmNeeded?: NavConfirmNeeded;
  withinLaunchDate: boolean;
};

const css = stylesheet({
  list: merge(versionedItemTableStyles, {
    $nest: {
      tbody: {
        $nest: {
          td: {
            paddingRight: 40,
          },
        },
      },
    },
  }),
});

function newPricingInfo(infoType: PricingEnum): PricingInfo {
  return {
    infoType,
    modifiedAt: '',
    modifiedBy: '',
    value: '',
    version: 0,
  };
}

function pricingDataToFormValues(isMXOrEU: boolean, pricing?: PricingInfo[]): PricingFormValues {
  const pricingInfoOrder = isMXOrEU ? pricingInfoTypeOrder.filter((info) => info !== PricingEnum.OurPrice) : pricingInfoTypeOrder;
  const pricingInfos = map(pricingInfoOrder, (infoType) => {
    const savedPricing = find(pricing, (price) => price.infoType === infoType);
    return {
      ...newPricingInfo(infoType),
      ...savedPricing,
    };
  });

  return {
    attributes: {},
    pricingInfos,
  };
}

function determinePricingPublishStatus({
  currentPricingInfo,
  lastPublishedData,
}: {
  currentPricingInfo: PricingInfo;
  lastPublishedData?: PricingInfo[];
}): STATUSES {
  let status = STATUSES.DRAFT;

  if (lastPublishedData) {
    const publishedData = lastPublishedData as PricingInfo[];
    const lastPublishedElement: PricingInfo | undefined = publishedData.find((p) => p.infoType === currentPricingInfo.infoType);

    const currentElement: PricingInfo = currentPricingInfo;

    if (lastPublishedElement && lastPublishedElement.version + 1 === currentElement.version) {
      if (currentElement?.publishResult?.status) {
        // First attempt to just use the status we've stored during validation
        status = currentElement.publishResult.status as STATUSES;
      } else {
        // Otherwise, have the user refresh so that we can populate the field
        status = STATUSES.PUBLISH_PENDING;
      }
    } else if (lastPublishedElement) {
      status = STATUSES.DRAFT_WITH_ACTIVE;
    }
  }

  return status;
}

export const PricingTab: FC<PricingTabProps> = ({
  asin: asinFragment,
  attributes,
  marketplace,
  productId,
  navConfirmNeeded,
  withinLaunchDate,
}) => {
  const { asin, canPublishPricingAttributes, canPublishConfidentialAttributes } = asinFragment;
  const queryVariables = {
    asin,
    isLaunchPermOn,
    marketplace,
    productId,
  };
  const realm = getSymphonyRealmFromMarketplace(marketplace);

  const editableAttributes = useMemo(
    () => attributes.filter((attribute) => AttributeClassification[attribute.key] === 'pricing'),
    [
      attributes,
    ],
  );

  // For the marketplace MX, EU except IN - do not display our price
  const isUniqueMarketplace = (realm === 'EU' || marketplace === 'MX') && marketplace !== 'IN';

  const {
    data,
    loading,
    error: loadError,
    refetch: refetchPricing,
  } = usePricingQuery({
    variables: queryVariables,
  });
  const attributesQuery = useAttributesQuery({
    variables: queryVariables,
  });
  const [savePricing, { data: savePricingData, loading: saving, error: saveError }] = useSavePricingMutation();
  const [validatePublishedPricingMutation, { data: activePricingData, loading: activeDataLoading }] = useValidatePublishedPricingMutation();

  const hasPublishPermissions = canPublishPricingAttributes.allowed;
  const hasAttrPublishPermissions = canPublishConfidentialAttributes.allowed;

  const [publishedDataFormValues, setPublishedDataFormValues] = useState<PricingFormValues | null>(null);

  const pricingValues = data?.pricing ?? [];
  const pricingInput = pricingValues.map((pricingValue) => ({
    infoType: pricingValue.infoType,
    value: pricingValue.value,
    version: pricingValue.version,
  }));

  const { data: publishedData, loading: publishedDataLoading } = useLastPublishedPricingQuery({
    variables: {
      input: {
        ...queryVariables,
        infoTypes: pricingInput,
      },
    },
  });

  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [isErrorsModalOpen, setIsErrorsModalOpen] = useState<boolean>(false);
  const openPublishModal = () => {
    if (publishedData?.lastPublishedPricing) {
      const pricing = pricingDataToFormValues(isUniqueMarketplace, publishedData?.lastPublishedPricing);
      setPublishedDataFormValues(pricing);
    }

    setIsPublishModalOpen(true);
  };

  const [isCatalogPublishingErrorsModalOpen, setIsCatalogPublishingErrorsModalOpen] = useState<boolean>(false);
  const catalogPublishingErrors = {
    apiIssues: reducePublishingIssues(attributesQuery.data?.attributes),
    // Purposefully left blank for first revision.
    diffErrors: {},
  };
  const catalogPublishErrorsExist = hasPublishingErrors(catalogPublishingErrors);
  const openCatalogPublishErrorsModal = () => {
    setIsCatalogPublishingErrorsModalOpen(true);
  };

  const hasPublishedEver = data?.pricing.some((p) => !!p.lastPublishedDate && !!p.lastPublishedVersion);

  const onRefreshClick = async () => {
    await validatePublishedPricingMutation({
      variables: {
        input: {
          asin,
          marketplace,
          productId,
        },
      },
    });

    await refetchPricing();
    await attributesQuery.refetch();
  };

  useEffect(() => {
    validatePublishedPricingMutation({
      variables: {
        input: {
          asin,
          marketplace,
          productId,
        },
      },
    });
  }, [
    asin,
    marketplace,
    productId,
    validatePublishedPricingMutation,
  ]);

  let publishErrorCount = 0;
  const publishErrorInfoTypes: string[] = [];
  const currentStatuses: Record<string, STATUSES> = {
    ourPrice: STATUSES.DRAFT,
    ourPriceWithTax: STATUSES.DRAFT,
  };

  if (data && activePricingData) {
    data.pricing.forEach((p) => {
      const status = determinePricingPublishStatus({
        currentPricingInfo: p,
        lastPublishedData: publishedData?.lastPublishedPricing,
      });

      currentStatuses[p.infoType] = status;

      const lastPublishedElement = publishedData?.lastPublishedPricing.find((pi) => pi.infoType === p.infoType);
      if (lastPublishedElement && lastPublishedElement.version + 1 === p.version) {
        if (status !== STATUSES.PUBLISHED && status !== STATUSES.PUBLISH_PENDING) {
          // If status is publish failed
          // OR we've published but we don't have a result
          // OR we've published and our result success === false
          if (status === STATUSES.PUBLISH_FAILED || (p.lastPublishedVersion && (!p.publishResult || !p.publishResult.success))) {
            publishErrorCount++;
            publishErrorInfoTypes.push(p.infoType);
          }
        }
      } else if (p.publishResult && p.publishResult.status === STATUSES.PUBLISH_FAILED) {
        publishErrorCount++;
        publishErrorInfoTypes.push(p.infoType);
      }
    });
  }

  // Determine what is publishable
  let isOurPricePublishable: boolean | undefined;
  data?.pricing.forEach((p) => {
    if (
      [
        STATUSES.DRAFT,
        STATUSES.DRAFT_WITH_ACTIVE,
        STATUSES.PUBLISH_FAILED,
      ].includes(currentStatuses[p.infoType])
    ) {
      if (currentStatuses[p.infoType] === STATUSES.DRAFT && p.version === 0) {
        // Draft and have not yet saved
        return;
      }

      if (p.infoType.startsWith('ourPrice')) {
        isOurPricePublishable = true;
      }
    }
    return false;
  });

  // If ourPrice or ourPriceWithTax are publishable, ensure both are added to the array
  const publishableData: PricingInfo[] = [];
  if (data) {
    if (isOurPricePublishable) {
      if (isUniqueMarketplace) {
        publishableData.push(...data.pricing.filter((p) => p.infoType === 'ourPriceWithTax'));
      } else {
        publishableData.push(...data.pricing.filter((p) => p.infoType.startsWith('ourPrice')));
      }
    }
  }

  const hasPublishValidationError =
    activePricingData?.validatePublishedPricing && activePricingData.validatePublishedPricing.code !== '200';
  const publishValidationErrorMessage = activePricingData?.validatePublishedPricing?.message;

  // on form submit, save any changes
  const onSubmit = async (formVals: PricingFormValues, form: FormApi<PricingFormValues>): Promise<boolean> => {
    const formState = form.getState();
    const savedPricing = data?.pricing ?? [];
    const pricingToSave = getPricingInfoToSave({
      dirtyFields: keys(formState.dirtyFields).filter((key) => formState.dirtyFields[key]),
      formVals,
      savedPricing,
    });

    const savedAttributes = attributesQuery.data?.attributes ?? [];
    const attributesToSave = getAttributesToSave({
      dirtyFields: keys(formState.dirtyFields).filter((key) => formState.dirtyFields[key]),
      formVals,
      isPricingTab: true,
      savedAttributes,
    });
    try {
      const { errors, data: savedData } = await savePricing({
        // update our local state with the response from the mutation, which is the updated pricing
        update: (cache, mutationResult) => {
          if (!mutationResult.data) {
            return;
          }

          const succeededPricing = mutationResult.data.savePricing.succeeded;
          const pricingQueryResult = cache.readQuery({
            query: PricingDocument,
            variables: queryVariables,
          }) as PricingQuery;

          const newCachedPricing = getUpdatedPricing({
            cachedPricing: pricingQueryResult.pricing,
            succeededPricing,
          });

          cache.writeQuery({
            data: {
              pricing: newCachedPricing,
            },
            query: PricingDocument,
            variables: queryVariables,
          });
        },
        variables: {
          input: {
            asin,
            isLaunchPermOn,
            marketplace,
            pricingInfo: pricingToSave,
            productId,
          },
        },
      });

      const { errors: attrErrors, data: savedAttrData } = await saveAttributes({
        // update our local state with the response from the mutation, which is the updated attributes
        update: (cache, mutationResult) => {
          if (!mutationResult.data) {
            return;
          }

          const attributesQueryResult = cache.readQuery({
            query: AttributesDocument,
            variables: queryVariables,
          }) as AttributesQuery;

          const newCachedAttributes = getUpdatedAttributes({
            cachedAttributes: attributesQueryResult.attributes,
            succeededAttributes: mutationResult.data.saveAttributes.succeeded,
          });

          cache.writeQuery({
            data: {
              attributes: newCachedAttributes,
            },
            query: AttributesDocument,
            variables: queryVariables,
          });
        },
        variables: {
          input: {
            asin,
            attributes: attributesToSave,
            isLaunchPermOn,
            marketplace,
            productId,
          },
        },
      });

      return (
        isEmpty(errors) && isEmpty(attrErrors) && isEmpty(savedData?.savePricing.failed) && isEmpty(savedAttrData?.saveAttributes.failed)
      );
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
    } catch (e: any) {
      console.log(e.message);
      return false;
    }
  };

  const languagesInMarketplace = useMemo(
    () => sortBy(uniq(languageOptions.filter((opt) => opt.marketplace.startsWith(marketplace)).map((opt) => opt.language))),
    [
      marketplace,
    ],
  );
  const defaultLanguage = MARKETPLACE_TO_DEFAULT_LANGUAGE[marketplace as Marketplace];

  const [initialValues, setInitialValues] = useState<PricingFormValues>({
    ...pricingDataToFormValues(isUniqueMarketplace, data?.pricing),
    attributes: {},
  });
  const [saveAttributes, { data: saveAttributesData, loading: savingAttr, error: saveAttrError }] = useSaveAttributesMutation();
  const attributesInput = attributesQuery.data?.attributes ?? [];
  const attributesForLastPublished = attributesInput.map((attr) => ({
    key: attr.key,
    language: attr.language,
    removed: attr.removed,
    value: attr.value,
    version: attr.version,
  }));
  const lastPublishedVersionInput = {
    asin,
    attributes: attributesForLastPublished,
    isLaunchPermOn,
    marketplace,
    productId,
  };

  // This is ok as we should always have item_name.
  const { data: lastPublishedAttrData, loading: lastPublishedAttrLoading } = useLastPublishedVersionsQuery({
    variables: {
      input: lastPublishedVersionInput,
    },
  });

  // prevent the initial values from jittering when we save
  useEffect(() => {
    if (data && !loading && attributesQuery.data && !attributesQuery.loading) {
      const attrFormValues = attributeDataToFormValues(attributesQuery.data?.attributes, defaultLanguage).attributes;
      const pricingKeys = getPricingKeysForMarketplace(marketplace);
      const priceAttributes: AttributeKeyToObject = reduce(
        pricingKeys,
        (attrs, key) => {
          const compositeAttrKey = getCompositeAttrKey(defaultLanguage, key);
          const pricingValueKeys = getPricingKeyValueForMarketplace(marketplace, key) ?? new Set<string>();
          const attrInitValue =
            attrFormValues[compositeAttrKey] ??
            getDefaultAttributeValue({
              attributeName: key,
              language: defaultLanguage,
            });
          const attrValues = pick(attrInitValue.values[0], Array.from(pricingValueKeys.values()));
          return {
            ...attrs,
            [compositeAttrKey]: {
              ...attrInitValue,
              values: [
                attrValues,
              ],
            },
          };
        },
        {},
      );
      setInitialValues({
        ...pricingDataToFormValues(isUniqueMarketplace, data?.pricing),
        attributes: priceAttributes,
      });
    }
    if (publishedData && !publishedDataLoading && lastPublishedAttrData && !lastPublishedAttrLoading) {
      setPublishedDataFormValues({
        ...pricingDataToFormValues(isUniqueMarketplace, publishedData.lastPublishedPricing),
        ...attributeDataToFormValues(lastPublishedAttrData.lastPublishedVersions, defaultLanguage),
      });
    }
  }, [
    data,
    loading,
    publishedData,
    publishedDataLoading,
    attributesQuery,
    lastPublishedAttrData,
    lastPublishedAttrLoading,
    defaultLanguage,
    isUniqueMarketplace,
    marketplace,
  ]);

  const [isAttrPublishModalOpen, setIsAttrPublishModalOpen] = useState<boolean>(false);
  const [selectedAttributes, setSelectedAttributes] = useState<AttributeFormValue[]>([]);

  const [publishedAttrDataFormValues, setPublishedAttrDataFormValues] = useState<AttributeListFormValues | null>(null);
  const openAttrModal = (attributeLists: AttributeFormValue[]) => {
    if (lastPublishedAttrData?.lastPublishedVersions) {
      const attributes = attributeDataToFormValues(lastPublishedAttrData?.lastPublishedVersions, defaultLanguage);
      setPublishedAttrDataFormValues(attributes);
    }
    setSelectedAttributes(attributeLists);
    setIsAttrPublishModalOpen(true);
  };

  const decorators = useMemo<Array<Decorator<PricingFormValues>>>(
    () => [
      createDecorator<PricingFormValues>(
        {
          // update our price, our price with tax, uvp_list_price, list price with tax when list price is updated
          // list price value is hidden for MX.
          field: `attributes[${defaultLanguage}:list_price].values[0].value`,
          isEqual,
          updates: (listPriceValue: string, _fieldName, _allValues, prevValues) => {
            // if this is firing due to the initialization of the form, don't set values
            if (isEmpty(listPriceValue) || isEmpty(prevValues?.attributes)) {
              return {};
            }
            const pricingInfo = {
              [`attributes[${defaultLanguage}:list_price].values[0].value_with_tax`]: listPriceValue,
              [`attributes[${defaultLanguage}:uvp_list_price].values[0].value`]: listPriceValue,
              'pricingInfos[1].value': listPriceValue,
            };
            if (realm && realm === 'EU') {
              return pricingInfo;
            }
            return {
              ...pricingInfo,
              'pricingInfos[2].value': listPriceValue,
            };
          },
        },
        {
          // update uvp_list_price currency when list_price currency is updated
          field: `attributes[${defaultLanguage}:list_price].values[0].currency`,
          isEqual,
          updates: (listPriceValue: string, _fieldName, _allValues, prevValues) => {
            // if this is firing due to the initialization of the form, don't set values
            if (isEmpty(listPriceValue) || isEmpty(prevValues?.attributes)) {
              return {};
            }
            return {
              [`attributes[${defaultLanguage}:uvp_list_price].values[0].currency`]: listPriceValue,
            };
          },
        },
        {
          // update our price with tax, uvp_list_price when list price with tax is updated
          field: `attributes[${defaultLanguage}:list_price].values[0].value_with_tax`,
          isEqual,
          updates: (listPriceValue: string, _fieldName, _allValues, prevValues) => {
            // if this is firing due to the initialization of the form, don't set values
            if (!isUniqueMarketplace || isEmpty(listPriceValue) || isEmpty(prevValues?.attributes)) {
              return {};
            }
            // pricingInfos[1] has our price with tax since our price is hidden for MX and EU
            return {
              [`attributes[${defaultLanguage}:uvp_list_price].values[0].value`]: listPriceValue,
              'pricingInfos[1].value': listPriceValue,
            };
          },
        },
      ),
    ],
    [
      defaultLanguage,
      isUniqueMarketplace,
      realm,
    ],
  );

  return (
    <>
      <section className={classes('pb-4', loadingOverlayContainerClass)}>
        {hasPublishValidationError && (
          <Alert dismissible variant="danger">
            {publishValidationErrorMessage}
            <br />
            <br />
            Please try again. If this problem persists, please contact the Journeys team.
          </Alert>
        )}
        <Form
          decorators={decorators}
          initialValues={initialValues}
          initialValuesEqual={isEqual}
          mutators={{
            ...arrayMutators,
          }}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, values: formValues, form }) => {
            const error =
              loadError ??
              saveError ??
              (savePricingData && !isEmpty(savePricingData?.savePricing?.failed)
                ? {
                    message: savePricingData.savePricing.failed.map((failure) => {
                      if (failure.reason.toLowerCase().includes('conditional')) {
                        return `Save failed for ${failure.infoType}. Someone else made changes to that pricing information.
                    Please refresh your page to get the latest and try again.`;
                      }
                      return `Save failed for ${failure.infoType}. Reason: ${failure.reason}`;
                    }),
                  }
                : undefined);
            const attrError =
              attributesQuery.error ??
              saveAttrError ??
              (saveAttributesData && !isEmpty(saveAttributesData?.saveAttributes?.failed)
                ? {
                    message: saveAttributesData.saveAttributes.failed.map((failure) => {
                      if (failure.reason.toLowerCase().includes('conditional')) {
                        return `Save failed for attribute ${failure.key}. Someone else has made changes to that attribute record.
                Please refresh your page to get the latest and try again.`;
                      }
                      return `Save failed for attribute ${failure.key}. Reason: ${failure.reason}`;
                    }),
                  }
                : undefined);
            const hasAttrError = Boolean(attrError);
            const hasError = Boolean(error);
            const attributesToPublish = Object.values(formValues?.attributes);
            const formState = form.getState();
            const publishDisabled =
              !hasPublishPermissions ||
              // formState.dirty || temporarily allowing the pricing to be published even if there haven't been any changes to the values: https://t.corp.amazon.com/P54380063
              loading ||
              hasError ||
              attributesQuery.loading ||
              !withinLaunchDate;

            const publishAttrDisabled =
              !hasAttrPublishPermissions || formState.dirty || attributesQuery.loading || hasAttrError || !withinLaunchDate;
            const savingDisabled = !formState.dirty || loading;
            const formAttributeKeys = keys(formValues.attributes);

            return (
              <form onSubmit={handleSubmit}>
                <UnsavedChangesGuard form={form} navConfirmNeeded={navConfirmNeeded} />
                <div className="d-flex flex-row-reverse">
                  <Button
                    className="mb-2 ml-2"
                    data-cy="refresh-pricing-button"
                    disabled={!hasPublishedEver || activeDataLoading}
                    onClick={onRefreshClick}
                    type="button"
                    // biome-ignore lint/suspicious/noExplicitAny: legacy code
                    variant={'outline-summary-light' as any}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faSync} />
                    Refresh
                  </Button>
                  <Button
                    className="mb-2"
                    data-cy="save-pricing-button"
                    disabled={savingDisabled}
                    type="submit"
                    // biome-ignore lint/suspicious/noExplicitAny: legacy code
                    variant={'outline-summary-light' as any}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faSave} />
                    Save
                  </Button>
                </div>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <h1 className={h1Class} data-cy="tab-header">
                      List price
                    </h1>
                    <p className="text-muted pr-3 mb-4">
                      Use the below fields to schedule list price setting. When you are ready you may use the publish
                      <br />
                      button to publish these values to Catalog.
                    </p>
                  </div>
                  <div>
                    {hasAttrPublishPermissions && (
                      <Button
                        data-cy="publish-to-catalog-pricing"
                        disabled={publishAttrDisabled || isEmpty(attributesToPublish)}
                        onClick={() => openAttrModal(attributesToPublish)}
                        // biome-ignore lint/suspicious/noExplicitAny: legacy code
                        variant={'outline-summary-light' as any}
                      >
                        <FontAwesomeIcon className="mr-2" icon={faPaperPlane} />
                        Publish to Catalog
                      </Button>
                    )}
                  </div>
                  <div>
                    {hasAttrPublishPermissions && (
                      <Button
                        className="mb-2 ml-2"
                        data-cy="errors-catalog-button"
                        disabled={activeDataLoading || !catalogPublishErrorsExist}
                        onClick={() => openCatalogPublishErrorsModal()}
                        type="button"
                        // biome-ignore lint/suspicious/noExplicitAny: legacy code
                        variant={'outline-summary-light' as any}
                      >
                        <FontAwesomeIcon className="mr-2" icon={faExclamationTriangle} />
                        Catalog Issues
                      </Button>
                    )}
                  </div>
                </div>
                {attrError && (
                  <ErrorAlert
                    contextMessage="Failed to save attribute data"
                    errors={
                      Array.isArray(attrError.message)
                        ? attrError.message.map((msg) => new Error(msg))
                        : [
                            attrError,
                          ]
                    }
                  />
                )}
                <AttributeList
                  attributeType={'pricing' as AttributeType}
                  currentAttributes={editableAttributes}
                  defaultLanguage={defaultLanguage}
                  formAttributeKeys={formAttributeKeys}
                  isBundleAsin={false}
                  isPricingTab
                  languages={languagesInMarketplace}
                  lastPublishedAttributes={lastPublishedAttrData?.lastPublishedVersions}
                  marketplace={marketplace}
                  openModal={openAttrModal}
                  publishDisabled={publishAttrDisabled}
                />

                <div className="d-flex mt-5">
                  <div className="flex-grow-1">
                    <h1 className={h1Class}>Pricing</h1>
                    <p className="text-muted pr-3 mb-4">
                      Use the below fields to schedule our price. When you are ready
                      <br />
                      you may use the publish button to publish these values to Pricing Rules.
                    </p>
                  </div>
                  <div>
                    {hasPublishPermissions && (
                      <Button
                        data-cy="publish-to-pricing-rules"
                        disabled={publishDisabled}
                        onClick={() => openPublishModal()}
                        // biome-ignore lint/suspicious/noExplicitAny: legacy code
                        variant={'outline-summary-light' as any}
                      >
                        <FontAwesomeIcon className="mr-2" icon={faPaperPlane} />
                        Publish to Pricing Rules
                      </Button>
                    )}
                  </div>
                  <div>
                    <Button
                      className="mb-2 ml-2"
                      data-cy="errors-pricing-button"
                      disabled={!hasPublishedEver || activeDataLoading || publishErrorCount === 0}
                      onClick={() => setIsErrorsModalOpen(true)}
                      type="button"
                      // biome-ignore lint/suspicious/noExplicitAny: legacy code
                      variant={'outline-summary-light' as any}
                    >
                      <FontAwesomeIcon className="mr-2" icon={faExclamationTriangle} />
                      Pricing Issues ({publishErrorCount})
                    </Button>
                  </div>
                </div>
                {error && (
                  <ErrorAlert
                    contextMessage="Failed to load pricing data"
                    errors={
                      Array.isArray(error.message)
                        ? error.message.map((msg) => new Error(msg))
                        : [
                            error,
                          ]
                    }
                  />
                )}
                <table className={css.list}>
                  <thead>
                    <tr className={textMutedDarkClass}>
                      <th />
                      <th>VALUE</th>
                      <th>STATUS</th>
                      <th>OWNER</th>
                      <th>LAST MODIFIED</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <FieldArray isEqual={isEqual} name="pricingInfos">
                      {({ fields, meta }) =>
                        map(fields.value, (pricingInfo: PricingInfo, rowIndex) => {
                          if (pricingInfo.infoType === PricingEnum.Currency) {
                            return true;
                          }

                          const status = currentStatuses[pricingInfo.infoType];
                          const fieldLabel = pricingInfoToLabel[pricingInfo.infoType];
                          const dataCy = `pricingInfos-${fieldLabel}-field`;

                          return (
                            <tr key={pricingInfo.infoType}>
                              <td>{fieldLabel}</td>
                              <td>
                                <Field isEqual={isEqual} name={`pricingInfos[${rowIndex}].value`}>
                                  {(props: FieldRenderProps<string>) => (
                                    <>
                                      <FormControl
                                        autoComplete="off"
                                        data-cy={dataCy}
                                        isInvalid={Boolean(
                                          pricingInfo.infoType.includes('Limit') && meta.error && meta.touched && meta.submitFailed,
                                        )}
                                        placeholder={pricingInfo.infoType}
                                        type="text"
                                        {...props.input}
                                      />
                                      <FormControl.Feedback type="invalid">{props.meta.error}</FormControl.Feedback>
                                    </>
                                  )}
                                </Field>
                              </td>
                              <td data-cy={`status ${fieldLabel}`}>
                                <Status type={status} />
                              </td>
                              <td data-cy={`modifiedBy ${fieldLabel}`}>{pricingInfo.modifiedBy}</td>
                              <td>
                                <AppDate dataCy={`modifiedAt ${fieldLabel}`} date={pricingInfo.modifiedAt} />
                              </td>
                            </tr>
                          );
                        })
                      }
                    </FieldArray>
                  </tbody>
                </table>
              </form>
            );
          }}
        </Form>
        <div className="mt-4 mb-4">
          <p className="text-muted pr-3 mb-4">
            Quantity limits can no longer be modified in Journeys.{' '}
            <a
              href="https://w.amazon.com/bin/view/Retail_Pricing/Retail_Quantity_Limits/#HManagingManualQuantityLimits"
              rel="noopener noreferrer"
              target="_blank"
            >
              Learn more about managing manual quantity limits
            </a>
          </p>
        </div>
        <LoadingOverlay visible={loading || publishedDataLoading || activeDataLoading || attributesQuery.loading || saving || savingAttr} />
      </section>
      <PricingPublishModal
        asin={asin}
        handleModalClose={() => setIsPublishModalOpen(false)}
        isPublishModalOpen={isPublishModalOpen}
        marketplace={marketplace}
        pricingInfo={publishableData}
        productId={productId}
        publishedDataFormValues={publishedDataFormValues}
        publishedDataLoading={publishedDataLoading}
        successCallback={onRefreshClick}
      />
      <AttributePublishModal
        asin={asin}
        attributes={selectedAttributes}
        handleModalClose={() => setIsAttrPublishModalOpen(false)}
        isPublishModalOpen={isAttrPublishModalOpen}
        marketplace={marketplace}
        onPublishSuccess={() => attributesQuery.refetch()}
        productId={productId}
        publishedDataFormValues={publishedAttrDataFormValues}
        publishedDataLoading={lastPublishedAttrLoading}
      />
      <PricingErrorModal
        activeData={activePricingData?.validatePublishedPricing.publishedPricingInfos as PricingInfo[]}
        activeDataLoading={activeDataLoading}
        handleModalClose={() => setIsErrorsModalOpen(false)}
        isErrorsModalOpen={isErrorsModalOpen}
        pricingData={data?.pricing as PricingInfo[]}
        publishErrorInfoTypes={publishErrorInfoTypes}
      />
      <AttributePublishingErrorsModal
        handleModalClose={() => setIsCatalogPublishingErrorsModalOpen(false)}
        isPublishingErrorsModalOpen={isCatalogPublishingErrorsModalOpen}
        publishingErrors={catalogPublishingErrors}
      />
    </>
  );
};
